import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";

import { LANGUAGES, LANGUAGE_STORAGE_KEY, type Language, resolveLanguage } from "./config";
import { formattersFor, type Formatters } from "./format";
import { applyDocumentLanguage } from "./i18n";

/**
 * The UI-language context.
 *
 * Why a context on top of `react-i18next` rather than calling the library
 * directly: switching language has side effects that have nothing to do with
 * rendering text, and each needs to happen once per change. `i18next
 * .changeLanguage()` on its own would leave all of them behind.
 *
 *   1. **Persistence** — localStorage, so the choice survives a reload *without*
 *      a flash of the wrong language on first paint.
 *   2. **`<html lang>`** — a screen reader reads Vietnamese text with an
 *      English voice, and the browser offers the wrong hyphenation, if this is
 *      stale. It also picks the font stack for the script.
 *
 * There is deliberately **no account sync**: the choice lives in localStorage
 * only. The profile column was removed rather than kept as a stale mirror.
 *
 * ## The flash problem
 *
 * A choice made in `useEffect` lands one paint *after* mount, so the app would
 * render English and then swap to Vietnamese — a visible flash on every load for
 * exactly the readers who chose a non-default language. Two things prevent it:
 * the language is resolved and `i18next.init` is called at module scope in
 * `i18n.ts` (before the first render), and `index.html` runs a tiny inline
 * script that sets `<html lang>` before the bundle boots. localStorage is
 * therefore the only source of truth on load.
 */

export interface LanguageContextValue {
  /** Active language, narrowed to a supported code. */
  language: Language;
  /** BCP 47 tag for `Intl`, `<html lang>` and `SpeechSynthesis`. */
  locale: string;
  dir: "ltr" | "rtl";
  setLanguage: (language: Language) => void;
  /** Locale-aware formatters bound to the active language. */
  format: Formatters;
}

const LanguageContext = createContext<LanguageContextValue | null>(null);

function writeStoredLanguage(language: Language): void {
  try {
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
  } catch {
    // A blocked or full storage quota must not break the switch: the choice
    // still applies for this session.
    console.warn("[i18n] could not persist language preference");
  }
}

/**
 * Apply a language to the document.
 *
 * A thin re-export of the module-level helper. `<html lang>` is kept in step by
 * a `languageChanged` subscription in `i18n.ts` rather than by an effect here,
 * so it stays correct even when something outside React changes the language.
 * The provider calls this only on the one path that subscription cannot see:
 * a language it resolved itself rather than one i18next emitted.
 */
export function applyDocumentLanguageFor(language: Language): void {
  applyDocumentLanguage(language);
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const { i18n } = useTranslation();

  const [language, setLanguageState] = useState<Language>(() =>
    resolveLanguage(i18n.resolvedLanguage ?? i18n.language),
  );

  // Keep the document in step with the active language. Redundant with the
  // `languageChanged` subscription in `i18n.ts`, but it also covers the very
  // first mount, where React's state initialiser may already hold a language
  // i18next never emitted for.
  useEffect(() => {
    applyDocumentLanguageFor(language);
  }, [language]);

  const setLanguage = useCallback(
    (next: Language) => {
      const resolved = resolveLanguage(next);
      setLanguageState(resolved);
      void i18n.changeLanguage(resolved);
      writeStoredLanguage(resolved);
      applyDocumentLanguageFor(resolved);
    },
    [i18n],
  );

  const value = useMemo<LanguageContextValue>(
    () => ({
      language,
      locale: LANGUAGES[language].intlLocale,
      dir: LANGUAGES[language].dir,
      setLanguage,
      format: formattersFor(language),
    }),
    [language, setLanguage],
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

/**
 * Read the active language, or `null` outside a provider.
 *
 * Returns `null` rather than throwing so a component can fall back to plain
 * English in isolation — unit tests render single components without the whole
 * app tree, and a throw there would force a wrapper onto every one of them for
 * no benefit.
 */
export function useLanguage(): LanguageContextValue | null {
  return useContext(LanguageContext);
}

/**
 * Translate a key with the active language.
 *
 * The everyday entry point. `useTranslation()` on its own is correct but easy
 * to forget the `lng` argument on, and a `t` left on the default language
 * renders English in the middle of a Vietnamese page — a bug that looks like a
 * missing translation rather than a wrong argument.
 */
export function useTranslate() {
  const { t, i18n } = useTranslation();
  const language = resolveLanguage(i18n.resolvedLanguage ?? i18n.language);
  return useMemo(() => (key: string, options?: Record<string, unknown>) => t(key, { lng: language, ...options }), [t, language]);
}